/**
 * Vendors — new (S14a → "Add vendor" CTA target).
 *
 * Full-page GSTIN-first composer. Bank-detail fields are visible only as
 * step-up placeholders; the encrypted values are not posted through create.
 *
 * @see prototype/purchases/vendor-new.html — V1.1 vendor composer baseline
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { trpc } from "../../../../lib/trpc.ts";
import { EMPTY_FORM, VendorForm, valuesToCreatePayload } from "../../../../features/vendors/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/procurement/vendors/new")({
  component: NewVendorPage,
});

function NewVendorPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.vendors.create.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.vendors.list.invalidate(), utils.vendors.counts.invalidate()]);
    },
  });

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Procurement", to: "/" },
        { label: "Vendors", to: "/procurement/vendors" },
        { label: "New" },
      ]}
      contentClassName="max-w-none h-full"
      viewportClassName="p-0"
      disableViewportScroll
    >
      <VendorForm
        defaultValues={EMPTY_FORM}
        submitLabel="Save vendor"
        pendingLabel="Saving…"
        pending={create.isPending}
        serverError={create.error?.message ?? null}
        mode="create"
        backHref="/procurement/vendors"
        onSubmit={async (values, { next }) => {
          const payload = valuesToCreatePayload(values);
          const result = await create.mutateAsync(payload);
          // The save-menu drives `next`:
          //   open        → drill into the new vendor's detail page
          //   list        → back to /vendors (default for primary Save)
          //   add-another → stay on /vendors/new (VendorForm resets the
          //                 form locally after this resolves)
          if (next === "open") {
            void navigate({
              to: "/procurement/vendors/$vendorId",
              params: { vendorId: result.id },
            });
            return;
          }
          if (next === "add-another") {
            // Stay; the form resets itself once we resolve.
            return;
          }
          void navigate({ to: "/procurement/vendors" });
        }}
      />
    </WorkspaceShell>
  );
}
